import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const ComingSoon = () => {
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState(0);

  useEffect(() => {
    const calculateDaysLeft = () => {
      const targetDate = new Date('2025-12-01T00:00:00');
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      const days = Math.ceil(difference / (1000 * 60 * 60 * 24));
      setDaysLeft(Math.max(0, days));
    };

    calculateDaysLeft();
    const interval = setInterval(calculateDaysLeft, 1000 * 60 * 60);

    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num: number) => {
    return num.toString().padStart(3, '0').split('');
  };

  const digits = formatNumber(daysLeft);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
      {/* Modern Layered Background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Primary glow */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '120vmin',
            height: '120vmin',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, rgba(6, 182, 212, 0.25) 25%, rgba(6, 182, 212, 0.12) 50%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
        {/* Secondary outer glow */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '160vmin',
            height: '160vmin',
            background: 'radial-gradient(circle, transparent 40%, rgba(6, 182, 212, 0.08) 60%, transparent 80%)',
            filter: 'blur(120px)',
          }}
        />
        {/* Accent highlights */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full"
          style={{ filter: 'blur(60px)' }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-500/5 rounded-full"
          style={{ filter: 'blur(60px)' }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 max-w-5xl mx-auto">
        
        {/* DAY'S LEFT Label */}
        <div className="mb-8">
          <p className="text-xs md:text-sm tracking-[0.35em] text-gray-500 uppercase font-light">
            Day's Left
          </p>
        </div>

        {/* Modern Sleek Counter */}
        <div className="flex items-center justify-center gap-3 mb-16">
          {digits.map((digit, index) => (
            <div
              key={index}
              className="relative"
              style={{
                width: 'clamp(65px, 14vw, 110px)',
                height: 'clamp(85px, 18vw, 145px)',
              }}
            >
              {/* Modern card with glass morphism */}
              <div 
                className="w-full h-full rounded-2xl relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.1)',
                }}
              >
                {/* Subtle top highlight */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                
                {/* Number display */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="font-bold text-white font-mono"
                    style={{
                      fontSize: 'clamp(3rem, 10vw, 5.5rem)',
                      textShadow: '0 2px 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(6, 182, 212, 0.2)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {digit}
                  </span>
                </div>

                {/* Bottom glow effect */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none"
                  style={{
                    background: 'linear-gradient(to top, rgba(6, 182, 212, 0.1), transparent)',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Main Headline - Smaller */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight max-w-4xl">
          LaneRunner. The future of freight is almost here.
        </h1>

        {/* Subheadline - Smaller */}
        <p className="text-sm sm:text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-2xl">
          We're redefining load booking with AI-driven transparency, verified carriers, and effortless negotiation.
        </p>
      </div>

      {/* I'm an Admin Button - Bottom Right */}
      <div className="fixed bottom-8 right-8 z-30">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/auth')}
          className="border-white/20 bg-black/30 backdrop-blur-sm text-white/80 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all duration-300"
        >
          I'm an admin
        </Button>
      </div>
    </div>
  );
};

export default ComingSoon;
